import {useCallback, useContext, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import cx from 'classnames';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {UPDATE_DESIGN_OPTIONS} from '~/CreateFabricInvitation/reducers/fabricDesign/constants';
import {
  selectBackgroundColor,
  selectCurrentSection,
  selectEditingMobile,
  selectForceShowOnboardingModal,
  selectPremiumFeaturesPristine,
  selectShouldTogglePremiumBackground,
} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {selectIsPremium} from '~/CreateFabricInvitation/reducers/fabricTemplate/selectors';
import {useNavigator} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/NavBottom/hooks/useNavigator';
import {useBackground} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/Background/actions/useBackground';
import {BackgroundContainer} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/Background/BackgroundContainer';
import {BackgroundEditorMobile} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/Background/Mobile/BackgroundEditorMobile';
import {CardEditorMobile} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/CardEditor/Mobile/CardEditorMobile';
import {EnvelopeContainer} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/Envelope/EnvelopeContainer';
import {EnvelopeEditorMobile} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/Envelope/Mobile/EnvelopeEditorMobile';
import {EventCreateEditor} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/EventCreateEditor';
import {NavBottom} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/NavBottom/NavBottom';
import {Onboarding} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/Onboarding/Onboarding';
import {RevealEditorMobile} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/Reveal/Mobile/RevealEditorMobile';
import {RevealContainer} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/Reveal/RevealContainer';
import {EventCreateStickerParent} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/Sticker/EventCreateStickerContext';
import {VideoBackground} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/VideoBackground/VideoBackground';
import {Sections} from '~/CreateFabricInvitation/routes/EventCreateDesign/constants';
import {
  EventCreateContext,
  EventCreateParent,
} from '~/CreateFabricInvitation/routes/EventCreateDesign/EventCreateContext';
import {FabricContext} from '~/common/fabric/context/FabricContext';
import {useInMwEditDesignButtonAbTest} from '~/common/hooks/useInMwEditDesignButtonAbTest';
import {useKeyboardControls} from '~/CreateFabricInvitation/routes/EventCreateDesign/hooks/useKeyboardControls';
import {getBackgroundStyles} from '~/CreateFabricInvitation/routes/EventCreateDesign/utils/getBackgroundStyles';

import './EventCreateDesign.sass';

const sectionComponentsMap = {
  [Sections.CARD_DESIGN]: EventCreateEditor,
  [Sections.BACKGROUND]: BackgroundContainer,
  [Sections.ENVELOPE_LINER]: EnvelopeContainer,
  [Sections.REVEAL]: RevealContainer,
};

const sectionEditMobileMap = {
  [Sections.CARD_DESIGN]: CardEditorMobile,
  [Sections.BACKGROUND]: BackgroundEditorMobile,
  [Sections.ENVELOPE_LINER]: EnvelopeEditorMobile,
  [Sections.REVEAL]: RevealEditorMobile,
};

const EventCreateDesign = () => {
  const {canvas, bkgColor} = useContext(EventCreateContext);
  const {inIsolatedEditMode, setInIsolatedEditMode} = useContext(FabricContext);
  const editingMobile = useSelector(selectEditingMobile);
  const currentSection = useSelector(selectCurrentSection);
  const backgroundColor = useSelector(selectBackgroundColor);
  const premiumFeaturesPristine = useSelector(selectPremiumFeaturesPristine);
  const shouldTogglePremiumBackground = useSelector(selectShouldTogglePremiumBackground);
  const isPremium = useSelector(selectIsPremium);
  const [showEnvelopeFront, setShowEnvelopeFront] = useState(false);
  const NavBottomMobile = sectionEditMobileMap[currentSection];
  const isLargishAndUp = useIsLargishAndUp();
  const isMobile = !isLargishAndUp;
  const {selectedBackground} = useBackground(isMobile);
  const dispatch = useDispatch();
  const Section = sectionComponentsMap[currentSection];

  useKeyboardControls(canvas);

  useEffect(() => {
    canvas?.discardActiveObject()?.requestRenderAll();
  }, [canvas, isMobile]);

  const handleFlipEnvelope = useCallback((shouldFlip) => {
    setShowEnvelopeFront(shouldFlip);
  }, []);

  const onSelectionEvent = useCallback(
    (value) => {
      if (isMobile) {
        dispatch({
          type: UPDATE_DESIGN_OPTIONS,
          payload: {editingMobile: value},
        });
      }
    },
    [isMobile, dispatch]
  );

  useEffect(() => {
    const portal = document.getElementById('pbModalRoot');
    if (portal) {
      portal.style.position = 'unset';
    }
  }, []);

  useEffect(() => {
    const handleSelectionCreatedOrUpdated = () => onSelectionEvent(true);
    const handleSelectionCleared = () => onSelectionEvent(false);

    canvas?.on('selection:created', handleSelectionCreatedOrUpdated);
    canvas?.on('selection:updated', handleSelectionCreatedOrUpdated);
    canvas?.on('selection:cleared', handleSelectionCleared);

    return () => {
      canvas?.off('selection:created', handleSelectionCreatedOrUpdated);
      canvas?.off('selection:updated', handleSelectionCreatedOrUpdated);
      canvas?.off('selection:cleared', handleSelectionCleared);
    };
  }, [canvas, onSelectionEvent]);

  useEffect(() => {
    // NOTE: this starts the process of rendering gallery uploaded img on canvas in step 1
    const searchParams = new URLSearchParams(window.location.search);
    if (
      (searchParams.has('previous') &&
        (['design_your_own', 'all_design_your_own', 'graduation'].includes(
          searchParams.get('previous')
        ) ||
          ['upload+your+own', 'upload your own', 'design+your+own', 'design your own'].some((t) =>
            searchParams.getAll('invitation_type').includes(t)
          ) ||
          searchParams.get('previous').search(/dyo_uyo/) !== -1)) ||
      searchParams.get('search_term') === 'graduation'
    ) {
      dispatch({type: UPDATE_DESIGN_OPTIONS, payload: {shouldUseCachedGalleryPhoto: true}});
    }
  }, [dispatch]);

  const isInEditButtonTest = useInMwEditDesignButtonAbTest();
  const scrollable = !inIsolatedEditMode;

  const navigate = useNavigator({isMobile: true, disableAutoOpenEditor: true});

  useEffect(() => {
    setInIsolatedEditMode(isInEditButtonTest && editingMobile);
  }, [editingMobile, isInEditButtonTest, setInIsolatedEditMode]);

  useEffect(() => {
    // if in the test & not in isolatedEditMode, set back to the card template view.
    if ((isInEditButtonTest && !inIsolatedEditMode) || currentSection === 'photo_upload') {
      navigate(Sections.CARD_DESIGN);
    }
  }, [navigate, isInEditButtonTest, inIsolatedEditMode, currentSection]);

  const style = getBackgroundStyles({
    isPremium,
    premiumFeaturesPristine,
    shouldTogglePremiumBackground,
    backgroundColor,
    bkgColor,
    selectedBackground,
  });

  return (
    <div
      className={cx('event-create-design', {
        scrollable,
        'fabric-create-invitation-root--isolated': inIsolatedEditMode,
        'fabric-create-invitation-root--isolatedABTest': isInEditButtonTest,
      })}
      style={style}
    >
      <div className={cx('event-create-design__content', 'step1-card-design')}>
        <VideoBackground selectedBackground={selectedBackground} />
        {Section && (
          <Section showEnvelopeFront={showEnvelopeFront} flipEnvelope={handleFlipEnvelope} />
        )}
      </div>
      <div className="event-create-design__bottom">
        {isMobile && NavBottomMobile && <NavBottomMobile flipEnvelope={handleFlipEnvelope} />}
        <NavBottom editingMobile={editingMobile} />
      </div>
    </div>
  );
};

export const EventCreateDesignContainer = () => {
  const forceShowOnboardingModal = useSelector(selectForceShowOnboardingModal);

  return (
    <EventCreateParent>
      {forceShowOnboardingModal && <Onboarding />}
      <EventCreateStickerParent>
        <EventCreateDesign />
      </EventCreateStickerParent>
    </EventCreateParent>
  );
};
